import type { DayReport } from "../types/api";
import { getFunctionUrl } from "../config";
import { fetchArchivedAssetLinks } from "./assets";
import { getAuthToken } from "./auth";
import { getSupabaseClient } from "./supabase";

const PREVIEW_CACHE_TTL_MS = 45 * 60 * 1000;
const PREVIEW_EXPIRES_SECONDS = 3600;
const PREVIEW_WIDTH = 320;
const PREVIEW_HEIGHT = 240;
const PREVIEW_CONCURRENCY = 4;

type PreviewCacheEntry = {
  url: string;
  expiresAt: number;
  objectUrl: boolean;
};

const cache = new Map<string, PreviewCacheEntry>();

function validStoragePaths(report: DayReport): string[] {
  const pattern = /^[0-9a-fA-F-]{36}\//;
  return Array.from(
    new Set(
      (report.assets ?? [])
        .map((asset) => asset?.storage_path)
        .filter((path): path is string => typeof path === "string" && pattern.test(path)),
    ),
  );
}

function cacheKey(path: string): string {
  return `report-assets-preview/${PREVIEW_WIDTH}x${PREVIEW_HEIGHT}/${path}`;
}

function freshCachedUrl(key: string, now = Date.now()): string | null {
  const cached = cache.get(key);
  if (!cached) return null;
  if (cached.expiresAt > now) return cached.url;
  if (cached.objectUrl) URL.revokeObjectURL(cached.url);
  cache.delete(key);
  return null;
}

function cacheUrl(key: string, url: string, objectUrl: boolean): void {
  const previous = cache.get(key);
  if (previous?.objectUrl && previous.url !== url) URL.revokeObjectURL(previous.url);
  cache.set(key, {
    url,
    objectUrl,
    expiresAt: Date.now() + PREVIEW_CACHE_TTL_MS,
  });
}

async function signStoragePreview(path: string): Promise<string | null> {
  const { data, error } = await getSupabaseClient().storage.from("report-assets")
    .createSignedUrl(path, PREVIEW_EXPIRES_SECONDS, {
      transform: {
        width: PREVIEW_WIDTH,
        height: PREVIEW_HEIGHT,
        resize: "contain",
        quality: 65,
      },
    });
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

async function fetchDropboxPreview(previewPath: string): Promise<string | null> {
  const token = await getAuthToken();
  if (!token) return null;
  const response = await fetch(getFunctionUrl("dbvdw-api", previewPath), {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "image/*",
    },
  });
  if (!response.ok) return null;
  const blob = await response.blob();
  if (!blob.size) return null;
  return URL.createObjectURL(blob);
}

async function mapWithConcurrency<T>(
  items: T[],
  mapper: (item: T) => Promise<void>,
): Promise<void> {
  let nextIndex = 0;
  const worker = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      await mapper(items[index]);
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(PREVIEW_CONCURRENCY, items.length) },
      () => worker(),
    ),
  );
}

export async function signedAssetPreviewUrls(
  report: DayReport,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  const paths = validStoragePaths(report);
  if (!paths.length) return result;

  const missing = paths.filter((path) => {
    const cached = freshCachedUrl(cacheKey(path));
    if (cached) {
      result.set(path, cached);
      return false;
    }
    return true;
  });
  if (!missing.length) return result;

  const assetsById = new Map(
    (report.assets ?? [])
      .filter((asset) =>
        typeof asset?.report_asset_id === "string" &&
        typeof asset?.storage_path === "string"
      )
      .map((asset) => [asset.report_asset_id as string, asset.storage_path as string]),
  );

  const archivePreviewByPath = new Map<
    string,
    { previewPath: string; storagePurged: boolean }
  >();
  try {
    const archived = await fetchArchivedAssetLinks(report.day_report_id);
    for (const link of archived) {
      const path = assetsById.get(link.report_asset_id);
      if (path && link.preview_path) {
        archivePreviewByPath.set(path, {
          previewPath: link.preview_path,
          storagePurged: link.storage_purged === true,
        });
      }
    }
  } catch (error) {
    console.warn(
      "Dropbox-fotopreviews konden niet worden opgehaald; Storage-preview blijft fallback",
      error,
    );
  }

  const storageFallback: string[] = [];
  await mapWithConcurrency(missing, async (path) => {
    const archivePreview = archivePreviewByPath.get(path);
    if (!archivePreview) {
      storageFallback.push(path);
      return;
    }
    try {
      const url = await fetchDropboxPreview(archivePreview.previewPath);
      if (url) {
        cacheUrl(cacheKey(path), url, true);
        result.set(path, url);
        return;
      }
    } catch (error) {
      console.warn(
        `Dropbox-fotopreview kon niet worden geladen voor ${path}; Storage-preview blijft fallback`,
        error,
      );
    }
    if (!archivePreview.storagePurged) storageFallback.push(path);
  });

  await mapWithConcurrency(storageFallback, async (path) => {
    try {
      const url = await signStoragePreview(path);
      if (!url) return;
      cacheUrl(cacheKey(path), url, false);
      result.set(path, url);
    } catch (error) {
      console.warn(
        `Fotopreview kon niet worden getransformeerd voor ${path}; origineel blijft alleen via openen beschikbaar`,
        error,
      );
    }
  });

  return result;
}
